<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject } from "../lib/types";
  import { relativeDate } from "../lib/format";

  let { data, onproject }: { data: SurfaceSnapshot; onproject: (p: SurfaceProject) => void } = $props();

  const DOMAINS = ["business", "family", "health", "growth"] as const;
  type Domain = (typeof DOMAINS)[number];

  function rank(p: SurfaceProject): number {
    return p.priority === "high" ? 0 : p.priority === "medium" ? 1 : 2;
  }

  function projectsFor(domain: Domain): SurfaceProject[] {
    return [...data.projects]
      .filter((p) => p.status === "active" && p.domain === domain)
      .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
  }

  const grouped = $derived(
    DOMAINS.map((d) => ({ domain: d, projects: projectsFor(d) })).filter((g) => g.projects.length > 0),
  );

  const undomained = $derived(
    [...data.projects]
      .filter((p) => p.status === "active" && !p.domain)
      .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title)),
  );
</script>

<div class="page">
  <header class="hero">
    <h1>Areas</h1>
    <p class="subtitle">Projects grouped by life domain</p>
  </header>

  {#each grouped as g (g.domain)}
    <section>
      <h2 class="section-title"><span class="dot {g.domain}"></span> {g.domain}</h2>
      <ul class="grouped-list">
        {#each g.projects as p (p.slug)}
          <li>
            <button class="row" type="button" onclick={() => onproject(p)}>
              <div class="row-content">
                <div class="title-row">
                  <span class="title">{p.title}</span>
                  {#if p.priority === "high"}<span class="pill priority-high">high</span>{/if}
                  {#if p.priority === "medium"}<span class="pill priority-medium">med</span>{/if}
                </div>
                <div class="meta">
                  <span class="faint">{p.open_count} open</span>
                  {#if p.waiting_count > 0}<span class="sep">·</span><span class="faint">{p.waiting_count} waiting</span>{/if}
                  {#if p.due}<span class="sep">·</span><span class="faint">due {relativeDate(p.due)}</span>{/if}
                </div>
              </div>
              <svg class="chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <path d="M4.3 2.3a1 1 0 0 1 1.4 0l3 3a1 1 0 0 1 0 1.4l-3 3a1 1 0 1 1-1.4-1.4L6.6 6 4.3 3.7a1 1 0 0 1 0-1.4Z" fill="currentColor"/>
              </svg>
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/each}

  {#if undomained.length > 0}
    <section>
      <h2 class="section-title">Unassigned</h2>
      <ul class="grouped-list">
        {#each undomained as p (p.slug)}
          <li>
            <button class="row" type="button" onclick={() => onproject(p)}>
              <div class="row-content">
                <div class="title-row"><span class="title">{p.title}</span></div>
                <div class="meta"><span class="faint">{p.open_count} open</span></div>
              </div>
              <svg class="chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <path d="M4.3 2.3a1 1 0 0 1 1.4 0l3 3a1 1 0 0 1 0 1.4l-3 3a1 1 0 1 1-1.4-1.4L6.6 6 4.3 3.7a1 1 0 0 1 0-1.4Z" fill="currentColor"/>
              </svg>
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if grouped.length === 0 && undomained.length === 0}
    <p class="empty">No active projects. Add tags like <code>domain/business</code> to project frontmatter.</p>
  {/if}
</div>

<style>
  .page { padding: 8px 16px 140px; max-width: 720px; margin: 0 auto; }
  .hero { padding: 12px 4px 16px; }
  h1 {
    font-size: 34px;
    line-height: 1.05;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
  .subtitle { color: var(--fg-dim); font-size: 15px; margin: 4px 0 0; }

  .section-title {
    font-size: 13px;
    margin: 22px 16px 6px;
    color: var(--fg-dim);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  section { margin-bottom: 4px; }
  .empty { color: var(--fg-dim); padding: 20px 4px; font-size: 15px; }

  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list li { border-bottom: 0.5px solid var(--separator); }
  .grouped-list li:last-child { border-bottom: 0; }

  .row {
    display: flex;
    width: 100%;
    text-align: left;
    align-items: center;
    gap: 12px;
    padding: 13px 14px 13px 16px;
    transition: background 0.15s;
  }
  .row:active { background: var(--tap); }
  .row-content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .chevron { flex-shrink: 0; color: var(--fg-faint); }

  .title-row { display: flex; align-items: center; gap: 8px; }
  .title { font-size: 16px; font-weight: 500; flex: 1; min-width: 0; letter-spacing: -0.005em; }
  .meta { font-size: 12px; color: var(--fg-dim); }
  .sep { margin: 0 6px; color: var(--fg-faint); }

  code {
    background: var(--bg-elev-2);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
  }
</style>
